import { isApiError } from '@pa/api'
import {
  getGetProtectionQueryKey,
  getListClinicActivityQueryKey,
  usePutProtection,
  type ClinicSecurity,
  type ProtectionSettings,
} from '@pa/api/admin'
import { toastApiError } from '@pa/staff'
import { fmtDateTimeOf, Modal, Segmented, useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, RotateCcw, Trash2 } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm, type FieldPath } from 'react-hook-form'
import {
  changesOf,
  formValuesOf,
  limitCodes,
  overridesOf,
  validateForm,
  type ProtectionFormValues,
} from '../model/form'
import { limitLabel, MODE_LABELS, windowLabel } from '../model/labels'
import { MAX_ORIGINS, ORIGIN_MESSAGES } from '../model/origins'

/** The note under the origins (Frontend §7.2) and the toast after a save that changed them (API.md §7.15). */
export const REDEPLOY_NOTE = 'Changes reach clinic websites after the next patient app deploy'
export const SAVED_TOAST = 'Protection settings saved'

/** Server issue codes → our field messages. */
const ISSUE_MESSAGES: Record<string, string> = {
  origin_invalid: ORIGIN_MESSAGES.invalid,
  out_of_range: 'Out of range.',
  unknown_code: 'Not a known setting.',
  cli_only: 'Changed through the ops CLI only.',
}

/** An API issue path (`['overrides', 'limits', 'ip_lookups', 'hard']`) → the form field. */
function fieldOf(path: readonly (string | number)[]): string | null {
  const [root, key, a, b] = path
  if (root !== 'overrides') return null
  if (key === 'bookingCap') return a === 'days' ? 'capDays' : 'capCount'
  if (key === 'scoreChallengeMode') return 'botMode'
  if (key === 'limits' && typeof a === 'string') return b === 'soft' ? `limits.${a}.soft` : `limits.${a}.hard`
  if (key === 'allowedEmbedOrigins') return typeof a === 'number' ? `origins.${String(a)}.value` : 'origins'
  return null
}

const CLI_ROWS: { label: string; value: (s: ClinicSecurity) => string }[] = [
  { label: 'Counter challenges', value: (s) => MODE_LABELS[s.counterChallengeMode] },
  { label: 'Challenge at', value: (s) => `${String(s.challengePoints)} points` },
  {
    label: 'Score points',
    value: (s) => `0.1 → ${String(s.scorePoints['0.1'])}, 0.3 → ${String(s.scorePoints['0.3'])}`,
  },
  { label: 'Minimum fill time', value: (s) => `${String(s.fillTimeMinMs)} ms` },
  { label: 'Daily reCAPTCHA ceiling', value: (s) => `${String(s.dailyAssessmentCeiling)} assessments` },
  {
    label: 'Rule overrides',
    value: (s) => {
      const rules = Object.keys(s.ruleOverrides)
      return rules.length === 0 ? 'None' : rules.map((r) => `${r} (monitor)`).join(', ')
    },
  },
]

export interface ProtectionFormProps {
  clinicId: string
  /** The clinic's zone (the "last saved" time, Frontend §7.1). */
  timeZone: string
  settings: ProtectionSettings
}

/**
 * S-04 Protection settings (UserFlows S-04 Protection; Frontend §7.2; Security §3.8; D-37, D-57): booking cap, bot mode
 * Monitor / Enforce, the thresholds (each with its default) and "Reset to defaults", and the allowed embed origins
 * (validated here as on the server). Save asks for confirmation with the list of changes, then #55 (last write wins,
 * audited by the server). The CLI-only settings are shown read-only.
 */
export function ProtectionForm({ clinicId, timeZone, settings }: ProtectionFormProps) {
  const id = useId()
  const toast = useToast()
  const queryClient = useQueryClient()
  const put = usePutProtection()
  const codes = useMemo(() => limitCodes(settings.defaults), [settings.defaults])
  const initial = useMemo(() => formValuesOf(settings.effective, codes), [settings.effective, codes])
  const form = useForm<ProtectionFormValues>({ defaultValues: initial })
  const { register, control, handleSubmit, setError, clearErrors, reset, formState, getValues } = form
  const origins = useFieldArray({ control, name: 'origins' })
  const [confirming, setConfirming] = useState<{ values: ProtectionFormValues; changes: string[] } | null>(null)
  const errors = formState.errors

  const errorOf = (path: string): string | undefined => {
    const parts = path.split('.')
    let node: unknown = errors
    for (const p of parts) node = (node as Record<string, unknown> | undefined)?.[p]
    const message = (node as { message?: unknown } | undefined)?.message
    return typeof message === 'string' ? message : undefined
  }

  const onSave = handleSubmit((values) => {
    clearErrors()
    const problems = validateForm(values, settings.defaults)
    const entries = Object.entries(problems)
    if (entries.length > 0) {
      for (const [path, message] of entries) setError(path as FieldPath<ProtectionFormValues>, { message })
      return
    }
    setConfirming({ values, changes: changesOf(settings.effective, values) })
  })

  async function save(values: ProtectionFormValues) {
    try {
      const result = await put.mutateAsync({ clinicId, data: { overrides: overridesOf(values, settings.effective) } })
      queryClient.setQueryData(getGetProtectionQueryKey(clinicId), result)
      void queryClient.invalidateQueries({ queryKey: getListClinicActivityQueryKey(clinicId) })
      reset(formValuesOf(result.effective, codes))
      toast(SAVED_TOAST)
      if (result.rebuildRequired) toast(REDEPLOY_NOTE, 'info')
    } catch (err) {
      if (isApiError(err) && err.code === 'invalid_request' && err.issues?.length) {
        let mapped = false
        for (const issue of err.issues) {
          const field = fieldOf(issue.path)
          if (field) {
            setError(field as FieldPath<ProtectionFormValues>, {
              message: ISSUE_MESSAGES[issue.code] ?? 'Invalid value.',
            })
            mapped = true
          }
        }
        if (mapped) {
          toast('Check the highlighted fields.', 'error')
          return
        }
      }
      toastApiError(toast, err)
    } finally {
      setConfirming(null)
    }
  }

  const resetToDefaults = () => {
    const d = formValuesOf(settings.defaults, codes)
    const current = getValues()
    // Every S-04 field back to Security.md's defaults (the form is dirty; Save confirms).
    reset({ ...current, ...d }, { keepDefaultValues: true })
    clearErrors()
  }

  const field = (path: string) => {
    const message = errorOf(path)
    const errId = `${id}-${path.replaceAll('.', '-')}-err`
    return {
      'aria-invalid': message ? true : undefined,
      'aria-describedby': message ? errId : undefined,
      message: message ? (
        <span className="err-text" id={errId}>
          {message}
        </span>
      ) : null,
    }
  }
  const capCount = field('capCount')
  const capDays = field('capDays')

  return (
    <form className="card card-pad form-grid protection-form" onSubmit={(e) => void onSave(e)} noValidate>
      <h2 className="form-section-title" id={`${id}-title`}>
        Protection settings
      </h2>
      <p className="muted">
        {settings.updatedAt
          ? `Last saved ${fmtDateTimeOf(settings.updatedAt, timeZone)}${settings.updatedBy ? ` by ${settings.updatedBy.name}` : ''}.`
          : 'Using the default settings.'}
      </p>

      <fieldset className="field">
        <legend className="label">Booking cap</legend>
        <div className="cap-row">
          <input
            className="input num-input"
            inputMode="numeric"
            aria-label="Bookings allowed"
            {...register('capCount')}
            aria-invalid={capCount['aria-invalid']}
            aria-describedby={capCount['aria-describedby']}
          />
          <span>bookings per</span>
          <input
            className="input num-input"
            inputMode="numeric"
            aria-label="Days"
            {...register('capDays')}
            aria-invalid={capDays['aria-invalid']}
            aria-describedby={capDays['aria-describedby']}
          />
          <span>days</span>
        </div>
        <span className="hint">
          Per patient. Default {String(settings.defaults.bookingCap.count)} per{' '}
          {String(settings.defaults.bookingCap.days)} days; 1–20 per 7–30 days.
        </span>
        {capCount.message}
        {capDays.message}
      </fieldset>

      <div className="field">
        <span className="label" id={`${id}-mode`}>
          Bot mode
        </span>
        <Controller
          control={control}
          name="botMode"
          render={({ field: f }) => (
            <Segmented
              label="Bot mode"
              value={f.value}
              onChange={f.onChange}
              options={[
                { value: 'monitor', label: MODE_LABELS.monitor },
                { value: 'enforce', label: MODE_LABELS.enforce },
              ]}
            />
          )}
        />
        <span className="hint">
          Monitor only records who would have been asked to confirm they&apos;re not a robot. Enforce shows them the
          checkbox. Default {MODE_LABELS[settings.defaults.scoreChallengeMode]}.
        </span>
      </div>

      <div className="field">
        <div className="field-head">
          <span className="label" id={`${id}-limits`}>
            Thresholds
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={resetToDefaults}>
            <RotateCcw aria-hidden /> Reset to defaults
          </button>
        </div>
        <div className="table-wrap">
          <table className="table thresholds-table" aria-labelledby={`${id}-limits`}>
            <thead>
              <tr>
                <th>Limit</th>
                <th>Soft limit</th>
                <th>Hard limit</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => {
                const d = settings.defaults.limits[code] ?? {}
                const per = windowLabel(d.windowSec)
                const soft = field(`limits.${code}.soft`)
                const hard = field(`limits.${code}.hard`)
                return (
                  <tr key={code}>
                    <td>
                      <b style={{ fontWeight: 600 }}>{limitLabel(code)}</b>
                      {per && <span className="cell-sub">per {per}</span>}
                    </td>
                    <td>
                      {d.soft === undefined ? (
                        <span className="muted">—</span>
                      ) : (
                        <>
                          <input
                            className="input num-input"
                            inputMode="numeric"
                            aria-label={`${limitLabel(code)}: soft limit`}
                            {...register(`limits.${code}.soft`)}
                            aria-invalid={soft['aria-invalid']}
                            aria-describedby={soft['aria-describedby']}
                          />
                          <span className="cell-sub">Default {String(d.soft)}</span>
                          {soft.message}
                        </>
                      )}
                    </td>
                    <td>
                      {d.hard === undefined ? (
                        <span className="muted">—</span>
                      ) : (
                        <>
                          <input
                            className="input num-input"
                            inputMode="numeric"
                            aria-label={`${limitLabel(code)}: hard limit`}
                            {...register(`limits.${code}.hard`)}
                            aria-invalid={hard['aria-invalid']}
                            aria-describedby={hard['aria-describedby']}
                          />
                          <span className="cell-sub">Default {String(d.hard)}</span>
                          {hard.message}
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <span className="hint">
          Soft: a challenge or a flag. Hard: a block or a rate limit. Hard must be more than soft and at most 10 × the
          default.
        </span>
      </div>

      <fieldset className="field">
        <legend className="label">Allowed embed origins</legend>
        <span className="hint">
          The clinic websites allowed to show the booking widget, like https://www.clinic.example.
        </span>
        {origins.fields.map((o, i) => {
          const f = field(`origins.${String(i)}.value`)
          return (
            <div key={o.id} className="origin-row">
              <input
                className="input mono"
                placeholder="https://www.clinic.example"
                autoComplete="off"
                aria-label={`Allowed embed origin ${String(i + 1)}`}
                {...register(`origins.${i}.value`)}
                aria-invalid={f['aria-invalid']}
                aria-describedby={f['aria-describedby']}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                aria-label={`Remove origin ${String(i + 1)}`}
                onClick={() => {
                  origins.remove(i)
                }}
              >
                <Trash2 aria-hidden />
              </button>
              {f.message}
            </div>
          )
        })}
        {origins.fields.length < MAX_ORIGINS && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              origins.append({ value: '' })
            }}
          >
            <Plus aria-hidden /> Add website
          </button>
        )}
        {field('origins').message}
        <p className="info-note">{REDEPLOY_NOTE}.</p>
      </fieldset>

      <section className="field" aria-labelledby={`${id}-cli`}>
        <h3 className="label" id={`${id}-cli`}>
          Changed through the ops CLI
        </h3>
        <dl className="kv">
          {CLI_ROWS.map((r) => (
            <div key={r.label} className="kv-row">
              <dt>{r.label}</dt>
              <dd>{r.value(settings.effective)}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={!formState.isDirty || put.isPending}>
          Save
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={!formState.isDirty || put.isPending}
          onClick={() => {
            reset(initial)
          }}
        >
          Discard
        </button>
      </div>

      {confirming && (
        <ConfirmSaveDialog
          changes={confirming.changes}
          pending={put.isPending}
          onCancel={() => {
            setConfirming(null)
          }}
          onConfirm={() => void save(confirming.values)}
        />
      )}
    </form>
  )
}

/** The save confirmation (Frontend §7.2 "a confirm dialog that summarizes the changes"). */
export function ConfirmSaveDialog({
  changes,
  pending,
  onConfirm,
  onCancel,
}: {
  changes: string[]
  pending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const id = useId()
  return (
    <Modal onClose={onCancel} labelledBy={`${id}-title`} describedBy={`${id}-body`}>
      <div className="modal-head">
        <div>
          <h2 id={`${id}-title`}>Save protection settings?</h2>
        </div>
      </div>
      <div className="modal-body" id={`${id}-body`}>
        {changes.length === 0 ? (
          <p>Nothing has changed.</p>
        ) : (
          <>
            <p>These changes apply to the clinic&apos;s booking page within 30 seconds:</p>
            <ul className="change-list">
              {changes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </>
        )}
      </div>
      <div className="modal-foot">
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" disabled={pending} onClick={onConfirm}>
          Save changes
        </button>
      </div>
    </Modal>
  )
}
